import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { filterCountries } from '../data-provider/countries/normalize';
import type { Countries, CountryFilters } from '../models';
import * as countriesService from '../services/countries.service';

export const useCountries = (defaultFilters: Partial<CountryFilters & { searchParams: { ids: string[] } }> = {}) => {
  const { searchParams, ...otherDefaultFilters } = useMemo(() => defaultFilters, [defaultFilters]);
  const [filters, setFilters] = useState<Partial<CountryFilters>>(otherDefaultFilters);

  const cacheData = countriesService.getAll();

  const updateFilters = useCallback((newFilters: Partial<CountryFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const { data, isLoading, isError } = useQuery(countriesService.countriesQueryOptions());

  const missingIds = useMemo(() => {
    if (!searchParams?.ids?.length || !data?.map) {
      return [];
    }
    return searchParams.ids.filter((id) => !data?.map[id] && !cacheData?.map[id]);
  }, [searchParams?.ids, data?.map, cacheData?.map]);

  const mergedMap: Countries['map'] = useMemo(() => {
    const map: Countries['map'] = {
      ...(data?.map ?? {}),
      ...(cacheData?.map ?? {}),
    };

    if (data && missingIds.length) {
      for (const id of missingIds) {
        const country = data.map[id];
        if (country) {
          map[id] = country;
        }
      }
    }

    return map;
  }, [data, cacheData?.map, missingIds]);

  const filteredData = useMemo(
    () => filterCountries({ list: Object.values(mergedMap), map: mergedMap }, filters),
    [mergedMap, filters],
  );

  return {
    data: filteredData,
    isError,
    isLoading,
    updateFilters,
  };
};
